import type { SudokuGame } from "./sudokuGame";

/**
 * Result returned by strategy methods when they find a solution for a cell
 */
export interface Hint {
    /** The cell ID where the answer was found */
    id: number;
    /** The answer value for the cell */
    answer: string;
    /** The name of the strategy that found this result */
    strategy: string;
    /** Description function that explains how this result was found */
    description?: (hint: Hint) => (() => string)[];
    /** Optional data to store strategy-specific information to avoid recalculation */
    data?: {
        cellIds?: number[];
        candidate?: string;
        candidates?: string[];
        eliminationCellIds?: number[];
        eliminationCandidates?: string[];
        unitType?: string;
        patternType?: string;
        [key: string]: any; // Allow strategy-specific data
    };
}

export function appendPracticeLink(hintText: string, practicePath?: string): string {
    if (!practicePath) {
        return hintText;
    }

    return `${hintText}<div style="margin-top: 6px;"><a class="text-decoration-none" href="${practicePath}" target="_blank" rel="noopener noreferrer">Practice more with this</a></div>`;
}

/**
 * Handles code related to showing hints
 */
export const Hinter = {
    theGame: null as SudokuGame | null,
    executedHint: false as boolean,

    /**
     * Display a hint overlay for the current puzzle.
     *
     * @param hint - The hint payload and description steps to render.
     * @param theGame - Active game instance used for rendering and undo behavior.
     * @param onExecute - Optional callback fired when the final hint step executes.
     */
    show(hint: Hint, theGame: SudokuGame, onExecute?: () => void) {
        if (!hint.description) {
            return;
        }

        let $hintBox = $("#hint-box");
        // If it already exists, remove it.
        if ($hintBox.length) {
            Hinter.close(!Hinter.executedHint); // Keep the hint if executed
            return;
        }

        Hinter.theGame = theGame;
        Hinter.executedHint = false;

        // Get the array of UI update functions
        const descriptionFunctions = hint.description(hint);
        const practicePath = theGame.strategies[hint.strategy]?.practice;
        const $playgridWrapper = $("#playgrid-wrapper");
        const $sudokuTable = $("#sudokutable");
        const sudokuTableOffset = $sudokuTable.offset() || { top: 0, left: 0 };
        const wrapperOffset = $playgridWrapper.offset() || { top: 0, left: 0 };
        const wrapperWidth = $playgridWrapper.width();

        if (sudokuTableOffset.top - wrapperOffset.top < 80) {
            // Not enough space above the table (need ~90px) - show hint box below
            // This also handles the embed view.
            wrapperOffset.top = sudokuTableOffset.top + $sudokuTable.height();
        }

        $hintBox = $(`
            <div id='hint-box' style='
                position: absolute;
                top: ${wrapperOffset.top}px;
                left: ${wrapperOffset.left}px;
                background: white;
                border: 2px solid #ddd;
                border-radius: 8px;
                padding: 15px;
                box-shadow: 0 4px 12px rgba(0,0,0,0.15);
                width: ${wrapperWidth}px;
                min-height: 90px;
                z-index: 1000;
                text-align: center;
            '>
                <div id='hint-text' style='
                    margin-bottom: 10px;
                    font-size: 14px;
                    line-height: 1.3;
                    color: #333;
                    padding: 0 50px;
                    overflow: visible;
                '></div>
                <div id='hint-dots' style='
                    position: absolute;
                    bottom: 10px;
                    left: 50%;
                    transform: translateX(-50%);
                    display: flex;
                    gap: 6px;
                '></div>
                <button id='hint-left' class='btn btn-sm btn-light' style='
                    position: absolute;
                    bottom: 10px;
                    left: 10px;
                    width: 40px;
                    height: 32px;
                    font-size: 14px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                '>←</button>
                <button id='hint-right' class='btn btn-sm btn-primary' style='
                    position: absolute;
                    bottom: 10px;
                    right: 10px;
                    width: 40px;
                    height: 32px;
                    font-size: 14px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                '>→</button>
                <button id='hint-close' class='btn btn-sm btn-light' style='
                    position: absolute;
                    top: 10px;
                    right: 10px;
                    font-size: 20px;
                    color: #666;
                    width: 24px;
                    height: 24px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                '>×</button>
            </div>
        `);
        let currentDescriptionIdx = 0;
        const $uiRoot = $("body > main").first();
        ($uiRoot.length ? $uiRoot : $("body")).append($hintBox);
        const $hintText = $("#hint-text");
        const $hintDots = $("#hint-dots");

        const showCurrentDescription = () => {
            const hintText = descriptionFunctions[currentDescriptionIdx]();
            $hintText.html(
                currentDescriptionIdx === descriptionFunctions.length - 1
                    ? appendPracticeLink(hintText, practicePath)
                    : hintText
            );
        };

        // Save undo state before any hint execution
        Hinter.theGame?.saveUndo();

        // Create dots for navigation
        const updateDots = () => {
            $hintDots.empty();
            for (let i = 0; i < descriptionFunctions.length; i++) {
                const dot = $(`<div style='
                    width: 8px;
                    height: 8px;
                    border-radius: 50%;
                    background: ${i === currentDescriptionIdx ? '#5a7ac7' : '#ccc'};
                ' data-index="${i}"></div>`);
                $hintDots.append(dot);
            }
        };

        // Call the first function to get the hint text and perform UI updates
        showCurrentDescription();
        updateDots();
        if (descriptionFunctions.length === 1) {
            Hinter.executedHint = true;
            onExecute?.();
        }

        // Listen on buttons
        $("#hint-left").on("click", () => {
            if (currentDescriptionIdx === 0) {
                // User is at the first step, closing the hint
                Hinter.close(true); // Undo the hint
            } else {
                // If we're going back from the last step (where hint was executed), undo it
                if (Hinter.executedHint) {
                    Hinter.theGame?.undo();
                    Hinter.executedHint = false;
                    // Re-save undo state for potential re-execution
                    Hinter.theGame?.saveUndo();
                }

                currentDescriptionIdx = Math.max(0, currentDescriptionIdx - 1);
                showCurrentDescription();
                updateDots();
            }
        });
        $("#hint-right").on("click", () => {
            if (currentDescriptionIdx === descriptionFunctions.length - 1) {
                // User is at the end - closing the hint
                Hinter.close(false); // Keep the hint
            } else {
                currentDescriptionIdx = Math.min(descriptionFunctions.length - 1, currentDescriptionIdx + 1);
                showCurrentDescription();
                updateDots();

                // Check if we just moved to the last step (hint execution)
                if (currentDescriptionIdx === descriptionFunctions.length - 1) {
                    Hinter.executedHint = true;
                    onExecute?.();
                }
            }
        });
        $("#hint-close").on("click", () => {
            Hinter.close(!Hinter.executedHint); // Keep the hint if executed
        });
    },

    close(undoHint = true) {
        if (!Hinter.theGame) {
            return;
        }
        if (undoHint) {
            Hinter.theGame?.undo();
        }
        Hinter.theGame?.updateGameBoardUI(true);
        Hinter.theGame = null;
        Hinter.executedHint = false;

        // Remove event handlers to prevent memory leaks
        $("#hint-left").off("click");
        $("#hint-right").off("click");
        $("#hint-close").off("click");
        $("#hint-dots").off("click");

        // Remove the hint box from the DOM
        $("#hint-box").remove();
    }
};
